import { useState } from 'react';
import { Badge, Button } from 'react-daisyui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import PageContainer from '@/components/PageContainer';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import { DashboardCard } from '@/components/DashboardCard';
import { Spinner } from '@/components/Spinner';
import ErrorAlert from '@/components/ErrorAlert/ErrorAlert';
import { alertErrorMessage, alertMessageSuccess } from '@/utils/alerts';
import useSettings, { SettingRow } from '@/hooks/useSettings';
import ConfigSettingRow from './ConfigSettingRow';
import ConfigListRow from './ConfigListRow';
import SettingsDialog from './SettingsDialog';
import ListDialog from './ListDialog';

// VITE_ENV is 'local', 'test' or 'prod' depending on the build; the production deploy also writes
// VITE_ENV=production in .github/workflows/setup_env.sh. No label is shown when it is not set.
const environmentLabels: Record<string, string> = {
  local: 'Local',
  test: 'Test',
  prod: 'Production',
  production: 'Production',
};
const getEnvironmentLabel = (): string | null => {
  const env = (import.meta.env || process.env)?.VITE_ENV as string | undefined;
  if (!env) return null;
  return environmentLabels[env] ?? env;
};

// The last change saved from this browser, kept in localStorage so it can be undone. Only one is
// kept; saving another change replaces it. localStorage can be unavailable (private windows,
// blocked site data), so every read and write is wrapped and the page works without it.
interface LastChange {
  key: string;
  label: string;
  type: 'switch' | 'list';
  // The value before the change, which undo puts back.
  previous: unknown;
  // The value the change saved. Undo sends it as the expected value, so the api refuses the undo
  // when someone changed the setting again since.
  saved: unknown;
  changedAt: string;
}
const lastChangeStorageKey = 'settingsPage.lastChange';
const readLastChange = (): LastChange | null => {
  try {
    const stored = localStorage.getItem(lastChangeStorageKey);
    return stored ? (JSON.parse(stored) as LastChange) : null;
  } catch {
    return null;
  }
};
const writeLastChange = (change: LastChange | null) => {
  try {
    if (change) {
      localStorage.setItem(lastChangeStorageKey, JSON.stringify(change));
    } else {
      localStorage.removeItem(lastChangeStorageKey);
    }
  } catch {
    // The undo is a convenience; the change itself is already saved.
  }
};

function Settings() {
  const { settings, updateConfigSwitch, updateConfigList } = useSettings();
  // Each dialog keeps the row as the page showed it when the dialog was opened. Its value is what
  // the dialog describes and what the save sends as the expected value, so a reload of the page
  // data while the dialog is open cannot make the api accept a change the person did not see.
  const [dialog, setDialog] = useState<{
    row: SettingRow;
    isOn: boolean;
  } | null>(null);
  const [listDialog, setListDialog] = useState<SettingRow | null>(null);
  const [lastChange, setLastChange] = useState<LastChange | null>(
    readLastChange
  );
  const [confirmingUndo, setConfirmingUndo] = useState(false);
  const environment = getEnvironmentLabel();

  const rememberLastChange = (change: LastChange | null) => {
    writeLastChange(change);
    setLastChange(change);
    setConfirmingUndo(false);
  };

  const showError = (error: unknown, fallback: string) =>
    alertErrorMessage(
      error instanceof Error && error.message ? error.message : fallback
    );

  // Runs one save and closes its dialog either way. On success it shows a toast and remembers the
  // values before and after so the change can be undone. On failure it shows the api's message;
  // the settings have been reloaded by then, so the page shows what is saved now, and the dialog
  // is closed so it cannot keep showing the value it was opened with.
  const runSave = async (
    request: () => Promise<void>,
    successMessage: string,
    closeDialog: () => void,
    change: Omit<LastChange, 'changedAt'>
  ) => {
    try {
      await request();
      alertMessageSuccess(successMessage);
      rememberLastChange({ ...change, changedAt: new Date().toISOString() });
    } catch (error) {
      showError(error, 'The change could not be saved.');
    } finally {
      closeDialog();
    }
  };

  const saveSwitch = async (reason: string) => {
    if (!dialog) return;
    const { row, isOn } = dialog;
    await runSave(
      () => updateConfigSwitch(row.key, !isOn, row.value, reason),
      `${row.label} turned ${isOn ? 'off' : 'on'}`,
      () => setDialog(null),
      {
        key: row.key,
        label: row.label,
        type: 'switch',
        previous: row.value,
        saved: !isOn,
      }
    );
  };

  const saveList = async (value: (number | string)[], reason: string) => {
    if (!listDialog) return;
    const row = listDialog;
    await runSave(
      () => updateConfigList(row.key, value, row.value, reason),
      `${row.label} saved`,
      () => setListDialog(null),
      {
        key: row.key,
        label: row.label,
        type: 'list',
        previous: row.value,
        saved: value,
      }
    );
  };

  // Puts back the value from before the last change saved in this browser, after the person
  // confirms. The undo is saved and recorded like any other change, with the reason "Undo from the
  // settings page", and is refused when the setting no longer holds the value that change saved.
  const undoLastChange = async () => {
    if (!lastChange) return;
    const { key, label, type, previous, saved } = lastChange;
    const note = 'Undo from the settings page';
    try {
      if (type === 'switch') {
        await updateConfigSwitch(key, Boolean(previous), saved, note);
      } else {
        await updateConfigList(
          key,
          Array.isArray(previous) ? (previous as (number | string)[]) : [],
          saved,
          note
        );
      }
      alertMessageSuccess(`${label} put back`);
    } catch (error) {
      showError(error, 'The undo could not be saved.');
    } finally {
      rememberLastChange(null);
    }
  };

  // How the value undo would put back reads in the confirmation.
  const describePrevious = (change: LastChange) => {
    if (change.type === 'switch') return change.previous ? 'On' : 'Off';
    return Array.isArray(change.previous) && change.previous.length
      ? change.previous.map(String).join(', ')
      : 'an empty list';
  };

  let content: React.ReactNode;
  if (settings.error) {
    content = (
      <ErrorAlert
        message='There was a problem loading settings'
        error={settings.error as unknown}
        context='Settings'
      />
    );
  } else if (!settings.data) {
    content = (
      <div className='flex justify-center py-12'>
        <Spinner className='w-10 h-10' />
      </div>
    );
  } else {
    // One card per section, in the order the api returns them (sorted by section, then label).
    // Switches come first in each card, then lists.
    const sections = new Map<string, SettingRow[]>();
    settings.data.settings.forEach((row) => {
      const section = row.section || 'Other settings';
      sections.set(section, [...(sections.get(section) ?? []), row]);
    });
    content = (
      <div className='space-y-4'>
        {[...sections].map(([section, rows]) => (
          <DashboardCard key={section}>
            <section aria-label={section}>
              <h2 className='text-base font-bold mb-1'>{section}</h2>
              <ul>
                {rows
                  .filter((row) => row.valueType === 'switch')
                  .map((row) => (
                    <ConfigSettingRow
                      key={row.key}
                      row={row}
                      onToggle={(selected, isOn) =>
                        setDialog({ row: selected, isOn })
                      }
                    />
                  ))}
                {rows
                  .filter((row) => row.valueType !== 'switch')
                  .map((row) => (
                    <ConfigListRow
                      key={row.key}
                      row={row}
                      onEdit={setListDialog}
                    />
                  ))}
              </ul>
            </section>
          </DashboardCard>
        ))}
      </div>
    );
  }

  return (
    <PageContainer name='settings'>
      <DashboardWrapper>
        <div className='p-4'>
          <div className='mb-4'>
            <div className='flex items-center gap-3'>
              <h1 className='text-lg font-bold uppercase'>
                <FontAwesomeIcon
                  icon={faGear}
                  size='lg'
                  className='mr-4 text-white'
                />
                Settings
              </h1>
              {environment && (
                <Badge color='accent' variant='outline'>
                  {environment}
                </Badge>
              )}
            </div>
            <p className='text-sm text-gray-400 mt-1'>
              Changes apply to this environment only and are recorded with your
              name.
            </p>
          </div>
          {lastChange && (
            <div className='mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-base-300 p-3 text-sm'>
              {confirmingUndo ? (
                <>
                  <span>
                    Are you sure? Put {lastChange.label} back to{' '}
                    {describePrevious(lastChange)}?
                  </span>
                  <Button
                    type='button'
                    size='xs'
                    color='accent'
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={undoLastChange}
                  >
                    Yes, undo
                  </Button>
                  <Button
                    type='button'
                    size='xs'
                    color='accent'
                    variant='outline'
                    onClick={() => setConfirmingUndo(false)}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <span>
                    Your last change in this browser: {lastChange.label} (
                    {new Date(lastChange.changedAt).toLocaleString()})
                  </span>
                  <Button
                    type='button'
                    size='xs'
                    color='accent'
                    variant='outline'
                    onClick={() => setConfirmingUndo(true)}
                  >
                    Undo
                  </Button>
                </>
              )}
            </div>
          )}
          {content}
        </div>
        {dialog && (
          <SettingsDialog
            row={dialog.row}
            isOn={dialog.isOn}
            environment={environment}
            onConfirm={saveSwitch}
            onCancel={() => setDialog(null)}
          />
        )}
        {listDialog && (
          <ListDialog
            row={listDialog}
            entries={
              Array.isArray(listDialog.value)
                ? (listDialog.value as (number | string)[])
                : []
            }
            environment={environment}
            onConfirm={saveList}
            onCancel={() => setListDialog(null)}
          />
        )}
      </DashboardWrapper>
    </PageContainer>
  );
}

export default Settings;
export { Settings };
